import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import CompatibilityIcon from 'marketplace-ui-common/compatibility/components/CompatibilityIcon';
import { getPricingUrl } from 'marketplace-ui-common/utils/urls';
import { useMemo } from 'react';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { useInstallCompatibility } from '../hooks/useInstallCompatibility';
import { getLowestTier } from '../utils/tierUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const FeatureCompatibilityMessage = ({
  onCompatibilityModalOpen,
  onTrackInteraction = () => {},
  featureName
}) => {
  const {
    appContext,
    published,
    listingId
  } = useDetailsPageContext();
  const isCustomer = appContext === 'app';
  const {
    data: installCompatibilityData
  } = useInstallCompatibility({
    listingId: listingId !== null && listingId !== void 0 ? listingId : 0,
    published,
    appContext
  });
  const {
    featureCompatibilities,
    portalTiers
  } = (installCompatibilityData === null || installCompatibilityData === void 0 ? void 0 : installCompatibilityData.productInstallCompatibility) || {};
  const relevantFeature = useMemo(() => {
    return featureCompatibilities === null || featureCompatibilities === void 0 ? void 0 : featureCompatibilities.find(feature => feature.featureName === featureName);
  }, [featureCompatibilities, featureName]);
  const featureCompatibilityType = useMemo(() => {
    var _relevantFeature$comp, _relevantFeature$comp2, _relevantFeature$comp3;
    if (appContext === 'public') {
      return 'COMPATIBLE_NONE';
    }
    if (relevantFeature !== null && relevantFeature !== void 0 && (_relevantFeature$comp = relevantFeature.compatibleTiers) !== null && _relevantFeature$comp !== void 0 && _relevantFeature$comp.includes('HUBSPOT_FREE') || (relevantFeature === null || relevantFeature === void 0 || (_relevantFeature$comp2 = relevantFeature.compatibleTiers) === null || _relevantFeature$comp2 === void 0 ? void 0 : _relevantFeature$comp2.length) === 0) {
      return 'COMPATIBLE_ALL';
    }
    const tierIntersection = relevantFeature === null || relevantFeature === void 0 || (_relevantFeature$comp3 = relevantFeature.compatibleTiers) === null || _relevantFeature$comp3 === void 0 ? void 0 : _relevantFeature$comp3.filter(tier => portalTiers === null || portalTiers === void 0 ? void 0 : portalTiers.includes(tier));
    return tierIntersection !== null && tierIntersection !== void 0 && tierIntersection.length && (tierIntersection === null || tierIntersection === void 0 ? void 0 : tierIntersection.length) > 0 ? 'COMPATIBLE_ALL' : 'COMPATIBLE_NONE';
  }, [relevantFeature, portalTiers, appContext]);
  const {
    requiredPlanHub,
    requiredPlan,
    multipleHubOptionsAvailable
  } = useMemo(() => {
    var _relevantFeature$comp4;
    if (appContext === 'public') {
      return {
        requiredPlan: getLowestTier([...((relevantFeature === null || relevantFeature === void 0 ? void 0 : relevantFeature.compatibleTiers) || [])]) || 'HUBSPOT_FREE'
      };
    }
    const portalPlanHubTypes = [...new Set((portalTiers === null || portalTiers === void 0 ? void 0 : portalTiers.map(tier => String(tier).split('_')[0])) || [])];
    const featurePlanHubTypes = [...new Set((relevantFeature === null || relevantFeature === void 0 || (_relevantFeature$comp4 = relevantFeature.compatibleTiers) === null || _relevantFeature$comp4 === void 0 ? void 0 : _relevantFeature$comp4.map(tier => String(tier).split('_')[0])) || [])];
    if (portalPlanHubTypes.length > 1 && featurePlanHubTypes.length > 1 && !portalPlanHubTypes.some(hubType => featurePlanHubTypes.includes(hubType))) {
      return {
        multipleHubOptionsAvailable: true
      };
    } else if (featureCompatibilityType === 'COMPATIBLE_NONE') {
      var _relevantFeature$comp5;
      const planHub = String((portalTiers === null || portalTiers === void 0 ? void 0 : portalTiers[0]) || '').split('_')[0];
      const compatibleRelevantTiers = (relevantFeature === null || relevantFeature === void 0 || (_relevantFeature$comp5 = relevantFeature.compatibleTiers) === null || _relevantFeature$comp5 === void 0 ? void 0 : _relevantFeature$comp5.filter(tier => String(tier).includes(planHub))) || [];
      const lowestRequiredTier = getLowestTier(compatibleRelevantTiers) ||
      // if the user has a hubType in common with the feature tiers, we can use that
      getLowestTier([...((relevantFeature === null || relevantFeature === void 0 ? void 0 : relevantFeature.compatibleTiers) || []) // otherwise, we use the lowest tier of the feature tiers
      ]);
      return {
        requiredPlanHub: lowestRequiredTier === null || lowestRequiredTier === void 0 ? void 0 : lowestRequiredTier.split('_')[0],
        requiredPlan: lowestRequiredTier
      };
    } else {
      return {};
    }
  }, [portalTiers, featureCompatibilityType, relevantFeature, appContext]);
  const handleExplorePlanOptionsClick = () => {
    onTrackInteraction({
      action: 'click feature required compare by plan link'
    });
    if (onCompatibilityModalOpen) {
      onCompatibilityModalOpen();
    }
  };
  return /*#__PURE__*/_jsx(MediaObject, {
    align: "start",
    itemLeft: isCustomer ? /*#__PURE__*/_jsx(CompatibilityIcon, {
      compatibilityType: featureCompatibilityType
    }) : undefined,
    spacing: "extra-small",
    "data-test-id": "feature-card-compatibility",
    children: multipleHubOptionsAvailable && onCompatibilityModalOpen ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `detailsPage.compatibility.features.COMPATIBLE_NONE_MULTIPLE_HUBS_jsx`,
      options: {
        linkProps: {
          onClick: handleExplorePlanOptionsClick
        }
      },
      elements: {
        Bold: 'b',
        Link
      }
    }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: `detailsPage.compatibility.features.${featureCompatibilityType}_jsx`,
      options: {
        planName: requiredPlan ? I18n.text(`marketplace-ui-common.groupTags.tier.${requiredPlan}`) : '',
        linkProps: {
          external: true,
          href: getPricingUrl(appContext !== 'app', requiredPlanHub || ''),
          onClick: () => onTrackInteraction({
            action: 'click feature required plan compare by plan link',
            value: requiredPlanHub
          })
        }
      },
      elements: {
        Bold: 'b',
        Link
      }
    })
  });
};